/**
 * Содержимое карточки задачи — файлы, результаты, комментарии и связи между
 * задачами. Всё это сабресурсы `/v2/tasks/{id}/*`.
 *
 * Результаты и комментарии делят одну форму ответа (`commentListResponse`) —
 * это не ошибка контракта, а осознанное решение бэка: результат отличается от
 * комментария только тем, в каком списке лежит.
 */
import { http } from '@/shared/api';
import type { SchemaV2 } from '@/shared/api';

export type TaskAttachmentV2 =
  SchemaV2<'internal_tasks_handler.attachmentResponse'>;
export type TaskCommentV2 = SchemaV2<'internal_tasks_handler.commentResponse'>;
export type TaskCommentWriteV2 =
  SchemaV2<'internal_tasks_handler.commentWriteRequest'>;
export type TaskResultWriteV2 =
  SchemaV2<'internal_tasks_handler.resultWriteRequest'>;
export type TaskRelationV2 =
  SchemaV2<'internal_tasks_handler.relationResponse'>;
export type TaskRelationWriteV2 =
  SchemaV2<'internal_tasks_handler.relationWriteRequest'>;

const task = (id: string) => `/v2/tasks/${encodeURIComponent(id)}`;

/* ------------------------------------------------------------ файлы --- */

/**
 * Вложения задачи. По умолчанию — прикреплённые к самой задаче; `all` добавляет
 * файлы из чата и чек-листа, `messageId` / `itemId` сужают до одного источника.
 */
export async function fetchTaskFilesV2(
  taskId: string,
  params: { all?: boolean; messageId?: string; itemId?: string } = {}
): Promise<TaskAttachmentV2[]> {
  const { data } = await http.get<{ result?: TaskAttachmentV2[] }>(
    `${task(taskId)}/files`,
    { params }
  );

  return data.result ?? [];
}

/**
 * Загрузка файла. Отправляем multipart — бэк ждёт поле `file`. У голосового и
 * видео передаём длительность: без неё плеер в ленте не знает, сколько идёт
 * запись.
 */
export async function uploadTaskFileV2(
  taskId: string,
  file: File,
  link?: { messageId?: string; itemId?: string; durationMs?: number }
): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  // Привязка к сообщению чата или пункту чек-листа — теми же полями формы.
  if (link?.messageId) {
    form.append('messageId', link.messageId);
  }
  if (link?.itemId) {
    form.append('itemId', link.itemId);
  }
  if (link?.durationMs && link.durationMs > 0) {
    form.append('durationMs', String(Math.round(link.durationMs)));
  }
  const { data } = await http.post<{ id?: string }>(
    `${task(taskId)}/files`,
    form
  );

  return data.id ?? '';
}

export function deleteTaskFileV2(taskId: string, fileId: string) {
  return http.delete(`${task(taskId)}/files/${encodeURIComponent(fileId)}`);
}

/** Ссылка на скачивание — отдаём путь, качает браузер. */
export function taskFileDownloadUrlV2(taskId: string, fileId: string): string {
  return `${task(taskId)}/files/${encodeURIComponent(fileId)}/download`;
}

export type TaskTranscriptItemV2 =
  SchemaV2<'internal_tasks_handler.transcriptItemRequest'>;

/** Сохранить расшифровку аудио или видео — список фраз целиком, 204 без тела. */
export function saveTaskFileTranscriptV2(
  taskId: string,
  fileId: string,
  transcript: TaskTranscriptItemV2[]
) {
  return http.put(
    `${task(taskId)}/files/${encodeURIComponent(fileId)}/transcript`,
    { transcript }
  );
}

/* -------------------------------------------------------- результаты --- */

export async function fetchTaskResultsV2(
  taskId: string
): Promise<TaskCommentV2[]> {
  const { data } = await http.get<{ result?: TaskCommentV2[] }>(
    `${task(taskId)}/results`
  );

  return data.result ?? [];
}

export async function createTaskResultV2(
  taskId: string,
  payload: TaskResultWriteV2
): Promise<string> {
  const { data } = await http.post<{ id?: string }>(
    `${task(taskId)}/results`,
    payload
  );

  return data.id ?? '';
}

export function updateTaskResultV2(
  taskId: string,
  resultId: string,
  payload: TaskResultWriteV2
) {
  return http.patch(
    `${task(taskId)}/results/${encodeURIComponent(resultId)}`,
    payload
  );
}

export function deleteTaskResultV2(taskId: string, resultId: string) {
  return http.delete(`${task(taskId)}/results/${encodeURIComponent(resultId)}`);
}

/* ------------------------------------------------------ комментарии --- */

export async function fetchTaskCommentsV2(
  taskId: string
): Promise<TaskCommentV2[]> {
  const { data } = await http.get<{ result?: TaskCommentV2[] }>(
    `${task(taskId)}/comments`
  );

  return data.result ?? [];
}

export async function createTaskCommentV2(
  taskId: string,
  payload: TaskCommentWriteV2
): Promise<string> {
  const { data } = await http.post<{ id?: string }>(
    `${task(taskId)}/comments`,
    payload
  );

  return data.id ?? '';
}

/* ----------------------------------------------------------- связи --- */

export async function fetchTaskRelationsV2(
  taskId: string
): Promise<TaskRelationV2[]> {
  const { data } = await http.get<{ result?: TaskRelationV2[] }>(
    `${task(taskId)}/relations`
  );

  return data.result ?? [];
}

export function createTaskRelationV2(
  taskId: string,
  payload: TaskRelationWriteV2
) {
  return http.post(`${task(taskId)}/relations`, payload);
}

export function deleteTaskRelationV2(taskId: string, relatedId: string) {
  return http.delete(
    `${task(taskId)}/relations/${encodeURIComponent(relatedId)}`
  );
}
